<template>
  <div>
    <head>
      <meta charset="utf-8"/>
      <meta content="width=device-width, initial-scale=1.0" name="viewport"/>
      <link href="assets/css/bootstrap.min.css" rel="stylesheet" type="text/css"/>
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css"/>
      <link href="assets/css/app.min.css" rel="stylesheet" type="text/css"/>
      <link href="assets/css/custom.css" rel="stylesheet" type="text/css"/>
    </head>
    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar/>
      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- HEADER -->
            <div class="clm-header">
              <div class="clm-header-left">
                <div class="clm-header-icon"><i class="bx bx-wallet-alt"></i></div>
                <div>
                  <h1 class="clm-title">Offline Cash Management</h1>
                  <div class="clm-breadcrumb">
                    <i class="bx bx-home-alt"></i>
                    <span @click="gotTo('dashboard')" class="clm-bc-link">Dashboard</span>
                    <span class="clm-bc-sep">/</span>
                    <span>Cash Management</span>
                  </div>
                </div>
              </div>
              <button @click="loadAll" class="clm-refresh-btn" :disabled="loading">
                <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
              </button>
            </div>

            <!-- STAT CARDS -->
            <div class="clm-stat-row">
              <div class="clm-stat-card holding">
                <i class="bx bx-error-circle"></i>
                <div>
                  <span class="clm-stat-val">{{ outstandingSummary.totalOfficers }}</span>
                  <span class="clm-stat-lbl">Officers Holding</span>
                </div>
              </div>
              <div class="clm-stat-card amount">
                <i class="bx bx-money"></i>
                <div>
                  <span class="clm-stat-val">{{ formatPrice(outstandingSummary.totalAmount) }}</span>
                  <span class="clm-stat-lbl">Total Unsubmitted</span>
                </div>
              </div>
              <div class="clm-stat-card pending">
                <i class="bx bx-time-five"></i>
                <div>
                  <span class="clm-stat-val">{{ pendingSummary.count }}</span>
                  <span class="clm-stat-lbl">Pending Your Ack</span>
                </div>
              </div>
              <div class="clm-stat-card" :class="outstandingSummary.criticalCount > 0 ? 'critical' : 'safe'">
                <i class="bx" :class="outstandingSummary.criticalCount > 0 ? 'bxs-error' : 'bx-check-shield'"></i>
                <div>
                  <span class="clm-stat-val">{{ outstandingSummary.criticalCount }}</span>
                  <span class="clm-stat-lbl">Critical (3+ Days)</span>
                </div>
              </div>
            </div>

            <!-- TABS -->
            <div class="clm-tabs">
              <button @click="activeTab = 'outstanding'" :class="['clm-tab', activeTab === 'outstanding' ? 'active' : '']">
                <i class="bx bx-error-circle me-1"></i>
                Outstanding
                <span v-if="outstandingSummary.criticalCount > 0" class="clm-badge critical">{{ outstandingSummary.criticalCount }}</span>
              </button>
              <button @click="activeTab = 'pending'" :class="['clm-tab', activeTab === 'pending' ? 'active' : '']">
                <i class="bx bx-time-five me-1"></i>
                Pending Acknowledgment
                <span v-if="pendingSummary.count > 0" class="clm-badge warn">{{ pendingSummary.count }}</span>
              </button>
              <button @click="activeTab = 'history'; loadHistory()" :class="['clm-tab', activeTab === 'history' ? 'active' : '']">
                <i class="bx bx-history me-1"></i>
                Collection History
              </button>
              <button @click="activeTab = 'limits'; loadOfficerLimits()" :class="['clm-tab', activeTab === 'limits' ? 'active' : '']">
                <i class="bx bx-slider me-1"></i>
                Officer Limits
              </button>
            </div>

            <div v-if="loading" class="clm-loading">
              <div class="spinner-border text-primary"></div>
              <p>Loading...</p>
            </div>

            <!-- TAB: OUTSTANDING (handoverStatus IS NULL) -->
            <div v-if="!loading && activeTab === 'outstanding'">

              <div v-if="criticalOfficers.length > 0" class="clm-critical-banner">
                <i class="bx bxs-error clm-banner-icon"></i>
                <div>
                  <strong>IMMEDIATE ACTION REQUIRED</strong>
                  <p>{{ criticalOfficers.length }} officer(s) holding offline cash for 3+ days without submitting.</p>
                  <div class="clm-banner-chips">
                    <span v-for="o in criticalOfficers" :key="o.idNo" class="clm-chip critical">
                      {{ o.officerName }} — {{ o.daysHolding }} days — {{ formatPrice(o.totalOutstanding) }}
                    </span>
                  </div>
                </div>
              </div>

              <div v-if="outstanding.length === 0" class="clm-empty">
                <i class="bx bx-check-shield" style="color:#10b981;font-size:3rem"></i>
                <h5 class="mt-2">All Clear</h5>
                <p class="text-muted">No officers have unsubmitted offline collections.</p>
              </div>

              <div v-else class="clm-cards-grid">
                <div v-for="o in outstanding" :key="o.idNo" class="clm-card" :class="'risk-' + o.riskLevel.toLowerCase()">

                  <div class="clm-card-top">
                    <div class="clm-card-officer">
                      <div class="clm-avatar" :class="'risk-' + o.riskLevel.toLowerCase()">{{ initial(o.officerName) }}</div>
                      <div>
                        <div class="clm-officer-name">{{ o.officerName }}</div>
                        <div class="clm-officer-sub">{{ o.phoneNumber }}</div>
                        <div class="clm-officer-sub"><i class="bx bx-map-pin"></i> {{ o.subCountyName }} / {{ o.wardName }}</div>
                      </div>
                    </div>
                    <span class="clm-risk-badge" :class="'risk-' + o.riskLevel.toLowerCase()">{{ o.riskLevel }}</span>
                  </div>

                  <div class="clm-metrics">
                    <div class="clm-metric" :class="o.daysHolding >= 3 ? 'danger' : o.daysHolding >= 1 ? 'warn' : ''">
                      <span class="clm-mval">{{ o.daysHolding }}</span>
                      <span class="clm-mlbl">DAYS HOLDING</span>
                    </div>
                    <div class="clm-metric">
                      <span class="clm-mval">{{ o.txCount }}</span>
                      <span class="clm-mlbl">TRANSACTIONS</span>
                    </div>
                    <div class="clm-metric danger">
                      <span class="clm-mval sm">{{ formatPrice(o.totalOutstanding) }}</span>
                      <span class="clm-mlbl">UNSUBMITTED</span>
                    </div>
                    <div class="clm-metric">
                      <span class="clm-mval">{{ o.percentageOfLimit }}%</span>
                      <span class="clm-mlbl">LIMIT USED</span>
                    </div>
                  </div>

                  <div class="clm-bar-wrap">
                    <div class="clm-bar-track">
                      <div class="clm-bar-fill" :class="'risk-' + o.riskLevel.toLowerCase()" :style="{ width: Math.min(o.percentageOfLimit, 100) + '%' }"></div>
                    </div>
                    <span class="clm-bar-label">{{ formatPrice(o.totalOutstanding) }} / {{ formatPrice(o.cashLimit) }}</span>
                  </div>

                  <div class="clm-toggle" @click="toggleBreakdown(o.idNo)">
                    <span><i class="bx bx-calendar me-1"></i>Day-by-Day Breakdown</span>
                    <i class="bx" :class="expanded.includes(o.idNo) ? 'bx-chevron-up' : 'bx-chevron-down'"></i>
                  </div>
                  <div v-if="expanded.includes(o.idNo)" class="clm-day-list">
                    <div v-for="day in o.dailyBreakdown" :key="day.collectionDate" class="clm-day-row">
                      <span class="clm-day-date">{{ formatDay(day.collectionDate) }}</span>
                      <div class="clm-day-right">
                        <span class="clm-day-tx">{{ day.txCount }} txns</span>
                        <span class="clm-day-amt">{{ formatPrice(day.dayTotal) }}</span>
                      </div>
                    </div>
                    <div class="clm-oldest-note">
                      <i class="bx bx-time-five"></i>
                      Holding since <strong>{{ formatDateTime(o.oldestTxDate) }}</strong>
                    </div>
                  </div>

                  <div class="clm-card-actions">
                    <a :href="'tel:' + o.phoneNumber" class="clm-action call"><i class="bx bx-phone"></i> Call</a>
                    <button @click="openFlag(o)" class="clm-action flag"><i class="bx bx-flag"></i> Flag</button>
                    <button @click="openCollect(o)" class="clm-action collect"><i class="bx bx-money-withdraw"></i> Collect Cash</button>
                  </div>
                </div>
              </div>
            </div>

            <!-- TAB: PENDING ACKNOWLEDGMENT (handoverStatus = PENDING) -->
            <div v-if="!loading && activeTab === 'pending'">

              <div v-if="pending.length === 0" class="clm-empty">
                <i class="bx bx-inbox" style="color:#9ca3af;font-size:3rem"></i>
                <h5 class="mt-2">No Pending Submissions</h5>
                <p class="text-muted">No officers have submitted collections awaiting your acknowledgment.</p>
              </div>

              <div v-else class="clm-cards-grid">
                <div v-for="o in pending" :key="o.idNo" class="clm-card pending-card">

                  <div class="clm-card-top">
                    <div class="clm-card-officer">
                      <div class="clm-avatar pending-av">{{ initial(o.officerName) }}</div>
                      <div>
                        <div class="clm-officer-name">{{ o.officerName }}</div>
                        <div class="clm-officer-sub">{{ o.phoneNumber }}</div>
                        <div class="clm-officer-sub"><i class="bx bx-map-pin"></i> {{ o.subCountyName }} / {{ o.wardName }}</div>
                      </div>
                    </div>
                    <span class="clm-risk-badge pending-badge">PENDING</span>
                  </div>

                  <div class="clm-metrics">
                    <div class="clm-metric">
                      <span class="clm-mval">{{ o.txCount }}</span>
                      <span class="clm-mlbl">TRANSACTIONS</span>
                    </div>
                    <div class="clm-metric danger">
                      <span class="clm-mval sm">{{ formatPrice(o.totalAmount) }}</span>
                      <span class="clm-mlbl">SUBMITTED AMOUNT</span>
                    </div>
                    <div class="clm-metric">
                      <span class="clm-mval">{{ formatDay(o.oldestTx) }}</span>
                      <span class="clm-mlbl">FROM</span>
                    </div>
                    <div class="clm-metric">
                      <span class="clm-mval">{{ formatDay(o.latestTx) }}</span>
                      <span class="clm-mlbl">TO</span>
                    </div>
                  </div>

                  <div class="clm-ack-confirm-note">
                    <i class="bx bx-info-circle"></i>
                    Confirm you have physically received <strong>{{ formatPrice(o.totalAmount) }}</strong>
                    from {{ o.officerName }}. Officer's limit resets after acknowledgment.
                  </div>

                  <div class="clm-card-actions">
                    <a :href="'tel:' + o.phoneNumber" class="clm-action call"><i class="bx bx-phone"></i> Call</a>
                    <button @click="openAck(o, 'REJECTED')"    class="clm-action flag"><i class="bx bx-x"></i> Reject</button>
                    <button @click="openAck(o, 'ACKNOWLEDGED')" class="clm-action ack"><i class="bx bx-check-double"></i> Acknowledge</button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- ACKNOWLEDGE / REJECT MODAL -->
        <div class="modal fade" id="ackModal" tabindex="-1">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content clm-modal">
              <div class="clm-modal-hdr" :class="ackAction === 'REJECTED' ? 'danger' : ''">
                <h5>
                  <i class="bx me-2" :class="ackAction === 'REJECTED' ? 'bx-x' : 'bx-check-double'"></i>
                  {{ ackAction === 'REJECTED' ? 'Reject Handover' : 'Acknowledge Receipt' }}
                </h5>
                <button class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
              </div>
              <div class="modal-body p-4" v-if="ackingOfficer">
                <div class="clm-ack-box">
                  <div class="clm-ack-row"><span>Officer</span><strong>{{ ackingOfficer.officerName }}</strong></div>
                  <div class="clm-ack-row"><span>Phone</span><span>{{ ackingOfficer.phoneNumber }}</span></div>
                  <div class="clm-ack-row"><span>Amount</span><strong class="text-danger">{{ formatPrice(ackingOfficer.totalAmount) }}</strong></div>
                  <div class="clm-ack-row"><span>Transactions</span><span>{{ ackingOfficer.txCount }}</span></div>
                </div>
                <div v-if="ackAction === 'ACKNOWLEDGED'" class="clm-ack-confirm-note mt-3">
                  <i class="bx bx-info-circle"></i>
                  By confirming, you acknowledge physical receipt of
                  <strong>{{ formatPrice(ackingOfficer.totalAmount) }}</strong>.
                  Officer's limit will reset immediately.
                </div>
                <div v-if="ackAction === 'REJECTED'" class="mt-3">
                  <label class="clm-lbl">Rejection reason <span class="text-danger">*</span></label>
                  <textarea v-model="ackReason" class="form-control" rows="3" placeholder="Reason for rejection..."></textarea>
                  <div class="clm-ack-reject-note mt-2">
                    <i class="bx bx-error"></i>
                    Officer's transactions revert to NULL — they must resubmit.
                  </div>
                </div>
              </div>
              <div class="modal-footer border-0 px-4 pb-4">
                <button class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                <button @click="submitAck"
                        :class="['clm-btn', ackAction === 'REJECTED' ? 'danger' : 'primary']"
                        :disabled="(ackAction === 'REJECTED' && !ackReason) || submittingAck">
                  <span v-if="submittingAck" class="spinner-border spinner-border-sm me-2"></span>
                  {{ ackAction === 'REJECTED' ? 'Reject' : 'Confirm Receipt' }}
                </button>
              </div>
            </div>
            <!-- TAB: HISTORY (handoverStatus = SUBMITTED) -->
            <div v-if="activeTab === 'history'">

              <div class="clm-toolbar">
                <input v-model="historyFilter.dateFrom" type="date" class="clm-datepicker" @change="loadHistory"/>
                <input v-model="historyFilter.dateTo"   type="date" class="clm-datepicker" @change="loadHistory"/>
                <input v-model="historyFilter.officer"  type="text" class="clm-search" placeholder="Search officer..." @input="loadHistory"/>
                <button @click="loadHistory" class="clm-refresh-btn" :disabled="loadingHistory">
                  <i class="bx bx-refresh" :class="{ 'bx-spin': loadingHistory }"></i> Refresh
                </button>
                <div class="clm-history-total" v-if="historyGrandTotal > 0">
                  Total Collected: <strong>{{ formatPrice(historyGrandTotal) }}</strong>
                </div>
              </div>

              <div v-if="loadingHistory" class="clm-loading">
                <div class="spinner-border text-primary"></div>
                <p>Loading history...</p>
              </div>

              <template v-if="!loadingHistory && historyLoaded">
                <div v-if="history.length === 0" class="clm-empty">
                  <i class="bx bx-folder-open" style="color:#9ca3af;font-size:3rem"></i>
                  <h5 class="mt-2">No History Found</h5>
                  <p class="text-muted">No collected offline transactions for the selected period.</p>
                </div>

                <div v-else class="table-responsive">
                  <table class="table clm-table">
                    <thead>
                    <tr>
                      <th>Officer</th>
                      <th>Phone</th>
                      <th>Transactions</th>
                      <th>Total Collected</th>
                      <th>First Transaction</th>
                      <th>Last Transaction</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="h in history" :key="h.idNo">
                      <td>
                        <div class="clm-table-officer">
                          <div class="clm-avatar-sm">{{ initial(h.officerName) }}</div>
                          <span>{{ h.officerName }}</span>
                        </div>
                      </td>
                      <td>{{ h.phoneNumber }}</td>
                      <td>{{ h.txCount }}</td>
                      <td class="fw-bold text-success">{{ formatPrice(h.totalAmount) }}</td>
                      <td>{{ formatDateTime(h.firstDate) }}</td>
                      <td>{{ formatDateTime(h.lastDate) }}</td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr class="clm-total-row">
                      <td colspan="2"><strong>TOTAL</strong></td>
                      <td><strong>{{ history.reduce((s, h) => s + Number(h.txCount), 0) }}</strong></td>
                      <td class="fw-bold text-success">{{ formatPrice(historyGrandTotal) }}</td>
                      <td colspan="2"></td>
                    </tr>
                    </tfoot>
                  </table>
                </div>
              </template>
            </div>

            <!-- TAB: OFFICER LIMITS -->
            <div v-if="activeTab === 'limits'">

              <div class="clm-toolbar">
                <input v-model="limitsSearch" type="text" class="clm-search" placeholder="Search officer name or ID..." @input="loadOfficerLimits"/>
                <button @click="loadOfficerLimits" class="clm-refresh-btn" :disabled="loadingLimits">
                  <i class="bx bx-refresh" :class="{ 'bx-spin': loadingLimits }"></i> Refresh
                </button>
              </div>

              <div v-if="loadingLimits" class="clm-loading">
                <div class="spinner-border text-primary"></div>
              </div>

              <div v-else class="table-responsive">
                <table class="table clm-table">
                  <thead>
                  <tr>
                    <th>Officer</th>
                    <th>Phone</th>
                    <th>Location</th>
                    <th>Current Limit</th>
                    <th>Currently Holding</th>
                    <th></th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="o in officerLimits" :key="o.idNo">
                    <td>
                      <div class="clm-table-officer">
                        <div class="clm-avatar-sm">{{ initial(o.names) }}</div>
                        <div>
                          <div>{{ o.names }}</div>
                          <small class="text-muted">{{ o.idNo }}</small>
                        </div>
                      </div>
                    </td>
                    <td>{{ o.phoneNumber }}</td>
                    <td>{{ o.subCountyName }} / {{ o.wardName }}</td>
                    <td class="fw-bold">{{ formatPrice(o.offlineCashLimit) }}</td>
                    <td :class="o.currentHolding > 0 ? 'fw-bold text-danger' : 'text-muted'">
                      {{ o.currentHolding > 0 ? formatPrice(o.currentHolding) : '—' }}
                    </td>
                    <td>
                      <button @click="openSetLimit(o)" class="clm-edit-btn">
                        <i class="bx bx-edit"></i> Set Limit
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!loadingLimits && officerLimits.length === 0">
                    <td colspan="6" class="text-center text-muted py-4">No officers found</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </div>
        <div class="modal fade" id="collectModal" tabindex="-1">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content clm-modal">
              <div class="clm-modal-hdr collect">
                <h5><i class="bx bx-money-withdraw me-2"></i>Collect Cash from Officer</h5>
                <button class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
              </div>
              <div class="modal-body p-4" v-if="collectingOfficer">
                <div class="clm-ack-box">
                  <div class="clm-ack-row"><span>Officer</span><strong>{{ collectingOfficer.officerName }}</strong></div>
                  <div class="clm-ack-row"><span>Phone</span><span>{{ collectingOfficer.phoneNumber }}</span></div>
                  <div class="clm-ack-row"><span>Transactions</span><span>{{ collectingOfficer.txCount }}</span></div>
                  <div class="clm-ack-row"><span>Amount to Collect</span><strong class="text-danger">{{ formatPrice(collectingOfficer.totalOutstanding) }}</strong></div>
                  <div class="clm-ack-row"><span>Holding Since</span><span>{{ formatDateTime(collectingOfficer.oldestTxDate) }}</span></div>
                </div>
                <div class="clm-collect-confirm-note">
                  <i class="bx bx-info-circle"></i>
                  You are confirming that you have <strong>physically received
                  {{ formatPrice(collectingOfficer.totalOutstanding) }}</strong> from
                  {{ collectingOfficer.officerName }}.
                  All {{ collectingOfficer.txCount }} transaction(s) will be marked
                  <strong>SUBMITTED</strong> and the officer's limit will reset immediately.
                </div>
                <div class="mt-3">
                  <label class="clm-lbl">Notes (optional)</label>
                  <textarea v-model="collectNotes" class="form-control" rows="2" placeholder="e.g. Collected at Kajiado office, 09 Mar 2026..."></textarea>
                </div>
              </div>
              <div class="modal-footer border-0 px-4 pb-4">
                <button class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                <button @click="submitCollect" class="clm-btn collect" :disabled="submittingCollect">
                  <span v-if="submittingCollect" class="spinner-border spinner-border-sm me-2"></span>
                  Confirm Collection
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- SET LIMIT MODAL -->
        <div class="modal fade" id="limitModal" tabindex="-1">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content clm-modal">
              <div class="clm-modal-hdr">
                <h5><i class="bx bx-slider me-2"></i>Set Offline Cash Limit</h5>
                <button class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
              </div>
              <div class="modal-body p-4" v-if="settingLimitOfficer">
                <p class="text-muted mb-3">
                  Setting limit for <strong>{{ settingLimitOfficer.names }}</strong>
                  <br><small>Current limit: {{ formatPrice(settingLimitOfficer.offlineCashLimit) }}</small>
                </p>
                <div class="clm-preset-row mb-3">
                  <button v-for="p in limitPresets" :key="p"
                          @click="newLimitValue = p"
                          :class="['clm-preset', newLimitValue === p ? 'active' : '']">
                    {{ formatPrice(p) }}
                  </button>
                </div>
                <label class="clm-lbl">Or enter custom amount (KES)</label>
                <input v-model.number="newLimitValue" type="number" class="form-control" placeholder="e.g. 250000"/>
              </div>
              <div class="modal-footer border-0 px-4 pb-4">
                <button class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                <button @click="submitSetLimit" class="clm-btn primary" :disabled="!newLimitValue || savingLimit">
                  <span v-if="savingLimit" class="spinner-border spinner-border-sm me-2"></span>
                  Save Limit
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- FLAG MODAL -->
        <div class="modal fade" id="flagModal" tabindex="-1">
          <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content clm-modal">
              <div class="clm-modal-hdr danger">
                <h5><i class="bx bx-flag me-2"></i>Flag Officer</h5>
                <button class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
              </div>
              <div class="modal-body p-4" v-if="flaggingOfficer">
                <p class="text-muted mb-3">
                  Flagging <strong>{{ flaggingOfficer.officerName }}</strong> —
                  holding <strong>{{ formatPrice(flaggingOfficer.totalOutstanding) }}</strong>
                  for {{ flaggingOfficer.daysHolding }} day(s)
                </p>
                <label class="clm-lbl">Reason</label>
                <textarea v-model="flagReason" class="form-control" rows="3" placeholder="e.g. Unreachable for 3 days..."></textarea>
              </div>
              <div class="modal-footer border-0 px-4 pb-4">
                <button class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                <button @click="submitFlag" class="clm-btn danger" :disabled="!flagReason || submittingFlag">
                  <span v-if="submittingFlag" class="spinner-border spinner-border-sm me-2"></span>
                  Submit Flag
                </button>
              </div>
            </div>
          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6">© 2024 County Revenue System</div>
              <div class="col-sm-6 text-sm-end">Craft Silicon</div>
            </div>
          </div>
        </footer>
      </div>
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import { biller, execute } from "@/api";
import NavigationBar from "@/components/Navigation.vue";
import moment from "moment";

export default {
  name: "CashLimitManagement",
  components: { NavigationBar },

  data() {
    return {
      activeTab:          'outstanding',
      loading:            false,

      // getOutstandingOfflineCollections
      outstanding:        [],
      outstandingSummary: { totalOfficers: 0, totalAmount: 0, criticalCount: 0 },

      // getPendingSubmissions
      pending:            [],
      pendingSummary:     { count: 0, totalAmount: 0 },

      expanded:           [],

      // acknowledgeCashHandover
      ackingOfficer:      null,
      ackAction:          'ACKNOWLEDGED',
      ackReason:          '',
      submittingAck:      false,

      // flagOfficer
      flaggingOfficer:    null,
      flagReason:         '',
      submittingFlag:     false,

      // supervisorCollectCash
      collectingOfficer:  null,
      collectNotes:       '',
      submittingCollect:  false,

      // getOfflineCollectionHistory
      history:            [],
      historyGrandTotal:  0,
      loadingHistory:     false,
      historyLoaded:      false,
      historyFilter:      { dateFrom: '', dateTo: '', officer: '' },

      // getOfficerLimits / setOfficerCashLimit
      officerLimits:       [],
      loadingLimits:       false,
      limitsSearch:        '',
      settingLimitOfficer: null,
      newLimitValue:       null,
      savingLimit:         false,
      limitPresets:        [100000, 200000, 300000, 500000],

      refreshTimer:       null
    };
  },

  computed: {
    criticalOfficers() {
      return this.outstanding.filter(o => o.riskLevel === 'CRITICAL');
    }
  },

  mounted() {
    this.loadAll();
    this.refreshTimer = setInterval(() => this.loadAll(), 5 * 60 * 1000);
    if (this.$route?.query?.tab) this.activeTab = this.$route.query.tab;
  },

  beforeUnmount() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  },

  methods: {

    async loadAll() {
      this.loading = true;
      await Promise.all([
        this.loadOutstanding(),
        this.loadPending()
      ]);
      this.loading = false;
    },

    // API: getOutstandingOfflineCollections
    async loadOutstanding() {
      try {
        const form = new FormData();
        form.append('function', 'getOutstandingOfflineCollections');
        const res = await execute(form, biller);
        if (res.data.success) {
          this.outstanding        = res.data.data.outstanding || [];
          this.outstandingSummary = res.data.data.summary     || {};
        }
      } catch (e) { console.error(e); }
    },

    // API: getPendingSubmissions
    async loadPending() {
      try {
        const form = new FormData();
        form.append('function', 'getPendingSubmissions');
        const res = await execute(form, biller);
        if (res.data.success) {
          this.pending        = res.data.data.pending  || [];
          this.pendingSummary = res.data.data.summary  || {};
        }
      } catch (e) { console.error(e); }
    },

    // API: acknowledgeCashHandover
    openAck(officer, action) {
      this.ackingOfficer = officer;
      this.ackAction     = action;
      this.ackReason     = '';
      new bootstrap.Modal(document.getElementById('ackModal')).show();
    },

    async submitAck() {
      if (this.ackAction === 'REJECTED' && !this.ackReason) return;
      this.submittingAck = true;
      try {
        const form = new FormData();
        form.append('function',    'acknowledgeCashHandover');
        form.append('officerIdNo', this.ackingOfficer.idNo);
        form.append('action',      this.ackAction);
        form.append('reason',      this.ackReason);
        const res = await execute(form, biller);
        if (res.data.success) {
          bootstrap.Modal.getInstance(document.getElementById('ackModal'))?.hide();
          this.loadAll();
        } else {
          alert(res.data.message || 'Action failed.');
        }
      } catch (e) { console.error(e); }
      finally { this.submittingAck = false; }
    },

    // API: flagOfficer
    openFlag(officer) {
      this.flaggingOfficer = officer;
      this.flagReason      = '';
      new bootstrap.Modal(document.getElementById('flagModal')).show();
    },

    // API: getOfficerLimits
    async loadOfficerLimits() {
      this.loadingLimits = true;
      try {
        const form = new FormData();
        form.append('function', 'getOfficerLimits');
        form.append('search',   this.limitsSearch);
        const res = await execute(form, biller);
        if (res.data.success) {
          this.officerLimits.splice(0, this.officerLimits.length, ...(res.data.data.officers || []));
        }
      } catch (e) { console.error(e); }
      finally { this.loadingLimits = false; }
    },

    // API: setOfficerCashLimit
    openSetLimit(officer) {
      this.settingLimitOfficer = officer;
      this.newLimitValue       = officer.offlineCashLimit;
      new bootstrap.Modal(document.getElementById('limitModal')).show();
    },

    async submitSetLimit() {
      if (!this.newLimitValue || this.newLimitValue <= 0) return;
      this.savingLimit = true;
      try {
        const form = new FormData();
        form.append('function',    'setOfficerCashLimit');
        form.append('officerIdNo', this.settingLimitOfficer.idNo);
        form.append('newLimit',    this.newLimitValue);
        const res = await execute(form, biller);
        if (res.data.success) {
          bootstrap.Modal.getInstance(document.getElementById('limitModal'))?.hide();
          this.loadOfficerLimits();
        } else {
          alert(res.data.message || 'Failed to update limit.');
        }
      } catch (e) { console.error(e); }
      finally { this.savingLimit = false; }
    },

    // API: getOfflineCollectionHistory
    async loadHistory() {
      this.loadingHistory = true;
      this.historyLoaded  = false;
      try {
        const form = new FormData();
        form.append('function',    'getOfflineCollectionHistory');
        form.append('dateFrom',    this.historyFilter.dateFrom);
        form.append('dateTo',      this.historyFilter.dateTo);
        form.append('officerIdNo', this.historyFilter.officer);
        const res = await execute(form, biller);
        if (res.data.success) {
          // Use splice+push to guarantee Vue 2 reactivity
          this.history.splice(0, this.history.length, ...(res.data.data.history || []));
          this.historyGrandTotal = res.data.data.grandTotal || 0;
        }
      } catch (e) { console.error(e); }
      finally {
        this.loadingHistory = false;
        this.historyLoaded  = true;
      }
    },

    // API: supervisorCollectCash
    openCollect(officer) {
      this.collectingOfficer = officer;
      this.collectNotes      = '';
      new bootstrap.Modal(document.getElementById('collectModal')).show();
    },

    async submitCollect() {
      this.submittingCollect = true;
      try {
        const form = new FormData();
        form.append('function',    'supervisorCollectCash');
        form.append('officerIdNo', this.collectingOfficer.idNo);
        form.append('notes',       this.collectNotes);
        const res = await execute(form, biller);
        if (res.data.success) {
          bootstrap.Modal.getInstance(document.getElementById('collectModal'))?.hide();
          alert(`✓ ${res.data.message}`);
          this.loadAll();
        } else {
          alert(res.data.message || 'Collection failed.');
        }
      } catch (e) { console.error(e); }
      finally { this.submittingCollect = false; }
    },

    async submitFlag() {
      if (!this.flagReason) return;
      this.submittingFlag = true;
      try {
        const form = new FormData();
        form.append('function',    'flagOfficer');
        form.append('officerIdNo', this.flaggingOfficer.idNo);
        form.append('reason',      this.flagReason);
        form.append('amount',      this.flaggingOfficer.totalOutstanding);
        form.append('daysHolding', this.flaggingOfficer.daysHolding);
        await execute(form, biller);
        bootstrap.Modal.getInstance(document.getElementById('flagModal'))?.hide();
        alert(`${this.flaggingOfficer.officerName} flagged for review.`);
      } catch (e) { console.error(e); }
      finally { this.submittingFlag = false; }
    },

    toggleBreakdown(idNo) {
      const i = this.expanded.indexOf(idNo);
      if (i === -1) this.expanded.push(idNo); else this.expanded.splice(i, 1);
    },

    formatPrice(v)    { return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 2 }).format(parseFloat(v) || 0); },
    formatDay(d)      { return d ? moment(d).format('ddd DD MMM') : '—'; },
    formatDateTime(d) { return d ? moment(d).format('DD MMM YYYY HH:mm') : '—'; },
    initial(n)        { return n ? n.charAt(0).toUpperCase() : 'U'; },
    gotTo(r)          { this.$router.push(r); }
  }
};
</script>

<style scoped>
.clm-header { display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:1rem;
  border-radius:14px;padding:1.5rem 2rem;margin-bottom:1.25rem;
  background:linear-gradient(135deg,#1a1a2e 0%,#16213e 50%,#0f3460 100%);
  box-shadow:0 4px 24px rgba(0,0,0,.3); }
.clm-header-left { display:flex;align-items:center;gap:1rem; }
.clm-header-icon { width:56px;height:56px;border-radius:14px;display:flex;align-items:center;justify-content:center;
  font-size:1.75rem;background:rgba(99,102,241,.2);color:#818cf8; }
.clm-title { color:white;font-size:1.4rem;font-weight:700;margin:0 0 .25rem; }
.clm-breadcrumb { display:flex;align-items:center;gap:.4rem;color:rgba(255,255,255,.6);font-size:.8rem; }
.clm-bc-link { cursor:pointer; } .clm-bc-link:hover { color:white; } .clm-bc-sep { opacity:.4; }
.clm-refresh-btn { height:36px;padding:0 1rem;border:1px solid rgba(255,255,255,.2);
  border-radius:8px;background:rgba(255,255,255,.08);color:white;font-size:.875rem;
  cursor:pointer;display:flex;align-items:center;gap:.4rem;transition:all .2s; }
.clm-refresh-btn:hover:not(:disabled) { background:rgba(255,255,255,.15); }
.clm-refresh-btn:disabled { opacity:.5; }

.clm-stat-row { display:grid;grid-template-columns:repeat(4,1fr);gap:1rem;margin-bottom:1.25rem; }
.clm-stat-card { display:flex;align-items:center;gap:1rem;background:white;
  border-radius:12px;padding:1.25rem;box-shadow:0 2px 8px rgba(0,0,0,.06); }
.clm-stat-card i { font-size:2rem;flex-shrink:0; }
.clm-stat-card.holding i { color:#4f46e5; }
.clm-stat-card.amount  i { color:#d97706; }
.clm-stat-card.pending i { color:#2563eb; }
.clm-stat-card.critical i { color:#dc2626; }
.clm-stat-card.safe    i { color:#10b981; }
.clm-stat-val { display:block;font-size:1.3rem;font-weight:800;color:#1f2937;line-height:1; }
.clm-stat-lbl { font-size:.72rem;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.4px; }

.clm-tabs { display:flex;gap:.5rem;margin-bottom:1.25rem;border-bottom:2px solid #e5e7eb;padding-bottom:.5rem; }
.clm-tab { background:none;border:none;padding:.5rem 1.25rem;font-size:.875rem;font-weight:600;
  color:#6b7280;cursor:pointer;border-radius:8px 8px 0 0;transition:all .2s;display:flex;align-items:center;gap:.25rem; }
.clm-tab:hover { color:#374151;background:#f9fafb; }
.clm-tab.active { color:#4f46e5;background:#ede9fe;border-bottom:2px solid #4f46e5; }
.clm-badge { padding:.1rem .5rem;border-radius:99px;font-size:.65rem;font-weight:800;margin-left:.25rem; }
.clm-badge.critical { background:#fee2e2;color:#dc2626; }
.clm-badge.warn     { background:#fef3c7;color:#d97706; }

.clm-loading { text-align:center;padding:3rem;color:#6b7280; }
.clm-loading p { margin-top:.75rem; }
.clm-empty { text-align:center;padding:3rem; }

.clm-critical-banner { display:flex;gap:1rem;align-items:flex-start;background:#7f1d1d;
  border:1px solid #b91c1c;border-radius:12px;padding:1.25rem 1.5rem;margin-bottom:1.25rem;
  animation:pulse-b 2s ease-in-out infinite; }
@keyframes pulse-b { 0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,.4)}50%{box-shadow:0 0 0 6px rgba(239,68,68,0)} }
.clm-banner-icon { font-size:2rem;color:#fca5a5;flex-shrink:0; }
.clm-critical-banner strong { color:white;display:block;margin-bottom:.25rem; }
.clm-critical-banner p { color:#fee2e2;font-size:.875rem;margin:0 0 .5rem; }
.clm-banner-chips { display:flex;flex-wrap:wrap;gap:.5rem; }
.clm-chip.critical { background:rgba(239,68,68,.3);border:1px solid rgba(239,68,68,.5);
  color:#fecaca;padding:.2rem .75rem;border-radius:99px;font-size:.78rem;font-weight:600; }

.clm-cards-grid { display:grid;grid-template-columns:repeat(auto-fill,minmax(380px,1fr));gap:1.25rem; }
.clm-card { background:white;border-radius:14px;box-shadow:0 2px 8px rgba(0,0,0,.06);
  overflow:hidden;border-top:4px solid transparent;transition:box-shadow .2s; }
.clm-card:hover { box-shadow:0 8px 24px rgba(0,0,0,.1); }
.clm-card.risk-critical { border-top-color:#dc2626; }
.clm-card.risk-high     { border-top-color:#ea580c; }
.clm-card.risk-medium   { border-top-color:#d97706; }
.clm-card.risk-low      { border-top-color:#10b981; }
.clm-card.pending-card  { border-top-color:#2563eb; }

.clm-card-top { display:flex;justify-content:space-between;align-items:flex-start;padding:1.25rem 1.25rem .75rem; }
.clm-card-officer { display:flex;gap:.75rem; }
.clm-avatar { width:40px;height:40px;border-radius:50%;display:flex;align-items:center;
  justify-content:center;font-size:1rem;font-weight:700;color:white;flex-shrink:0; }
.clm-avatar.risk-critical { background:linear-gradient(135deg,#dc2626,#f87171); }
.clm-avatar.risk-high     { background:linear-gradient(135deg,#ea580c,#fb923c); }
.clm-avatar.risk-medium   { background:linear-gradient(135deg,#d97706,#fbbf24); }
.clm-avatar.risk-low      { background:linear-gradient(135deg,#059669,#34d399); }
.clm-avatar.pending-av    { background:linear-gradient(135deg,#1d4ed8,#60a5fa); }
.clm-officer-name { font-weight:700;font-size:.9375rem;color:#1f2937; }
.clm-officer-sub  { font-size:.78rem;color:#6b7280; }
.clm-risk-badge { display:inline-flex;align-items:center;padding:.25rem .75rem;border-radius:99px;
  font-size:.68rem;font-weight:800;text-transform:uppercase;letter-spacing:.5px;flex-shrink:0; }
.clm-risk-badge.risk-critical { background:rgba(220,38,38,.1);color:#dc2626;border:1px solid rgba(220,38,38,.3); }
.clm-risk-badge.risk-high     { background:rgba(234,88,12,.1);color:#ea580c;border:1px solid rgba(234,88,12,.3); }
.clm-risk-badge.risk-medium   { background:rgba(217,119,6,.1);color:#d97706;border:1px solid rgba(217,119,6,.3); }
.clm-risk-badge.risk-low      { background:rgba(5,150,105,.1);color:#059669;border:1px solid rgba(5,150,105,.3); }
.clm-risk-badge.pending-badge { background:rgba(37,99,235,.1);color:#2563eb;border:1px solid rgba(37,99,235,.3); }

.clm-metrics { display:grid;grid-template-columns:repeat(4,1fr);
  border-top:1px solid #f3f4f6;border-bottom:1px solid #f3f4f6; }
.clm-metric { padding:.875rem .5rem;text-align:center;border-right:1px solid #f3f4f6; }
.clm-metric:last-child { border-right:none; }
.clm-mval { display:block;font-size:1.1rem;font-weight:800;color:#1f2937;line-height:1;margin-bottom:.2rem; }
.clm-mval.sm { font-size:.8rem; }
.clm-mlbl { font-size:.55rem;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:.4px; }
.clm-metric.danger .clm-mval { color:#dc2626; }
.clm-metric.warn   .clm-mval { color:#d97706; }

.clm-bar-wrap  { padding:.875rem 1.25rem; }
.clm-bar-track { height:6px;background:#e5e7eb;border-radius:99px;overflow:hidden;margin-bottom:.35rem; }
.clm-bar-fill  { height:100%;border-radius:99px;transition:width .4s ease; }
.clm-bar-fill.risk-critical { background:linear-gradient(90deg,#dc2626,#f87171); }
.clm-bar-fill.risk-high     { background:linear-gradient(90deg,#ea580c,#fb923c); }
.clm-bar-fill.risk-medium   { background:linear-gradient(90deg,#d97706,#fbbf24); }
.clm-bar-fill.risk-low      { background:linear-gradient(90deg,#059669,#34d399); }
.clm-bar-label { font-size:.75rem;color:#6b7280; }

.clm-toggle { display:flex;justify-content:space-between;align-items:center;
  padding:.75rem 1.25rem;cursor:pointer;font-size:.8125rem;font-weight:600;
  color:#374151;border-top:1px solid #f3f4f6;transition:background .15s; }
.clm-toggle:hover { background:#f9fafb; }
.clm-day-list { padding:0 1.25rem .75rem; }
.clm-day-row  { display:flex;justify-content:space-between;align-items:center;
  padding:.4rem 0;border-bottom:1px solid #f9fafb;font-size:.8125rem; }
.clm-day-date  { color:#374151;font-weight:500; }
.clm-day-right { display:flex;gap:.75rem;align-items:center; }
.clm-day-tx    { color:#9ca3af;font-size:.75rem; }
.clm-day-amt   { color:#dc2626;font-weight:700; }
.clm-oldest-note { background:#fef3c7;border-radius:6px;padding:.5rem .75rem;
  font-size:.78rem;color:#92400e;display:flex;align-items:center;gap:.4rem;margin-top:.5rem; }

.clm-ack-confirm-note { margin:0 1.25rem .75rem;background:rgba(37,99,235,.06);
  border-left:3px solid #2563eb;padding:.75rem 1rem;border-radius:6px;
  font-size:.8rem;color:#1e40af;display:flex;align-items:flex-start;gap:.5rem; }
.clm-ack-confirm-note i { flex-shrink:0;margin-top:.1rem; }

.clm-card-actions { display:flex;gap:.5rem;padding:.875rem 1.25rem;border-top:1px solid #f3f4f6; }
.clm-action { flex:1;height:32px;border:none;border-radius:7px;font-size:.78rem;font-weight:600;
  cursor:pointer;display:inline-flex;align-items:center;justify-content:center;
  gap:.3rem;transition:all .2s;text-decoration:none; }
.clm-action.call { background:rgba(16,185,129,.1);color:#059669;border:1px solid rgba(16,185,129,.3); }
.clm-action.flag { background:rgba(239,68,68,.08);color:#dc2626;border:1px solid rgba(239,68,68,.3); }
.clm-action.ack  { background:rgba(37,99,235,.1);color:#2563eb;border:1px solid rgba(37,99,235,.3); }
.clm-action.collect { background:rgba(5,150,105,.1);color:#065f46;border:1px solid rgba(5,150,105,.4);font-weight:700; }
.clm-action:hover { filter:brightness(.92); }

.clm-modal { border:none;border-radius:14px;box-shadow:0 20px 60px rgba(0,0,0,.2);overflow:hidden; }
.clm-modal-hdr { padding:1.25rem 1.5rem;display:flex;justify-content:space-between;align-items:center;
  background:linear-gradient(135deg,#4f46e5,#7c3aed);color:white; }
.clm-modal-hdr.danger  { background:linear-gradient(135deg,#7f1d1d,#dc2626); }
.clm-modal-hdr.collect { background:linear-gradient(135deg,#065f46,#059669); }
.clm-modal-hdr h5 { margin:0;font-weight:600;display:flex;align-items:center; }
.clm-lbl { font-size:.73rem;font-weight:700;text-transform:uppercase;color:#6b7280;letter-spacing:.5px;display:block;margin-bottom:.35rem; }
.clm-ack-box { background:#f9fafb;border-radius:10px;padding:1rem;margin-bottom:1rem; }
.clm-ack-row { display:flex;justify-content:space-between;align-items:center;
  padding:.3rem 0;border-bottom:1px solid #f3f4f6;font-size:.8375rem; }
.clm-ack-row:last-child { border-bottom:none; }
.clm-ack-row span:first-child { color:#6b7280; }
.clm-ack-reject-note { display:flex;gap:.5rem;background:#fef3c7;border-radius:6px;
  padding:.5rem .75rem;font-size:.78rem;color:#92400e; }
.clm-btn { height:38px;padding:0 1.5rem;border:none;border-radius:7px;color:white;
  font-size:.875rem;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;transition:all .2s; }
.clm-btn.primary { background:linear-gradient(135deg,#4f46e5,#7c3aed); }
.clm-btn.danger  { background:linear-gradient(135deg,#dc2626,#ef4444); }
.clm-btn.collect { background:linear-gradient(135deg,#065f46,#059669); }
.clm-btn:disabled { opacity:.6;cursor:not-allowed; }
.clm-collect-confirm-note { background:rgba(5,150,105,.08);border-left:3px solid #059669;
  padding:.875rem 1rem;border-radius:6px;font-size:.8rem;color:#065f46;
  display:flex;align-items:flex-start;gap:.5rem;margin-top:.75rem; }
.clm-collect-confirm-note i { flex-shrink:0;margin-top:.1rem; }

.clm-history-total { margin-left:auto;font-size:.875rem;color:#374151; }
.clm-total-row { background:#f0fdf4;font-size:.875rem; }
.clm-total-row td { border-top:2px solid #d1fae5 !important; }

.clm-edit-btn { height:28px;padding:0 .75rem;border:1px solid #d1d5db;border-radius:6px;
  background:white;font-size:.78rem;cursor:pointer;display:inline-flex;align-items:center;
  gap:.25rem;transition:all .2s; }
.clm-edit-btn:hover { background:#f3f4f6; }

.clm-preset-row { display:flex;gap:.5rem;flex-wrap:wrap; }
.clm-preset { height:34px;padding:0 1rem;border:1px solid #d1d5db;border-radius:7px;
  background:white;font-size:.8125rem;cursor:pointer;font-weight:600;transition:all .2s; }
.clm-preset.active { border-color:#4f46e5;background:rgba(79,70,229,.1);color:#4f46e5; }
.clm-preset:hover:not(.active) { background:#f3f4f6; }

.clm-table { font-size:.8375rem; }
.clm-table thead tr { background:#f9fafb;font-size:.7rem;text-transform:uppercase;letter-spacing:.5px;color:#6b7280; }
.clm-table tbody tr:hover { background:#fafafa; }
.clm-table-officer { display:flex;align-items:center;gap:.6rem; }
.clm-avatar-sm { width:30px;height:30px;border-radius:50%;background:linear-gradient(135deg,#4f46e5,#818cf8);
  color:white;display:flex;align-items:center;justify-content:center;font-size:.8rem;font-weight:700;flex-shrink:0; }

@media(max-width:768px) {
  .clm-stat-row   { grid-template-columns:repeat(2,1fr); }
  .clm-cards-grid { grid-template-columns:1fr; }
  .clm-metrics    { grid-template-columns:repeat(2,1fr); }
}
</style>